// Deals API — typed-обёртки над 7 endpoints /api/deals/*.

import { apiClient } from './client';
import type { SendEmailResponse } from './email';
import type { Page } from './tasks';

/** Стадии сделки (синхронизировано с backend DealStage enum). */
export type DealStage = 'NEW' | 'QUALIFIED' | 'NEGOTIATION' | 'PROPOSAL' | 'WON' | 'LOST';

/**
 * Исторический seed четырёх кодов; не SSOT типа валюты (каталог GET /api/currencies).
 */
export const DEAL_CURRENCIES = ['RUB', 'USD', 'EUR', 'GBP'] as const;

/** ISO-4217 код валюты сделки (строка каталога, не union 4). */
export type DealCurrency = string;

/** DTO сделки (синхронизировано с backend DealDto). */
export interface Deal {
  id: string;
  title: string;
  stage: DealStage;
  amount: number | null;
  currency: DealCurrency;
  ownerUserId: string | null;
  contactId: string | null;
  companyId: string | null;
  departmentId: string;
  createdByUserId: string;
  /** Плановая дата закрытия (backend LocalDate, формат "YYYY-MM-DD"). */
  dueDate: string | null;
  /** Описание сделки (backend VARCHAR(4000) nullable, R18.2). */
  description: string | null;
  /** Фактическая дата закрытия — ставится backend'ом при переходе в WON/LOST (ISO Instant, nullable). */
  closedAt: string | null;
  /** Ближайшее будущее событие календаря по сделке (ISO Instant, nullable; только будущие, R20). */
  nextContactAt: string | null;
  /** Последнее прошедшее неотменённое событие календаря по сделке (ISO Instant, nullable, U-18). */
  lastContactAt: string | null;
  /** FK причины отказа при стадии LOST; null у открытых и легаси-LOST. */
  rejectionReasonId?: string | null;
  /** Имя причины отказа (JOIN каталога); null если причины нет. */
  rejectionReasonName?: string | null;
  createdAt: string;
  updatedAt: string;
  canceledAt: string | null;
  version: number;
  canceled: boolean;
  terminalStage: boolean;
}

/** Колонка дат-диапазона фильтра сделок (U-27). */
export type DealDateField = 'createdAt' | 'nextContactAt' | 'dueDate';

export interface DealFilters {
  page?: number;
  size?: number;
  /** Живой поиск: ILIKE по выбранным полям (≤100 символов, канон R33b / U-38). */
  q?: string;
  /** CSV-список полей поиска (whitelist DealSearchField: title,description,stage,currency,company_id,contact_id,owner_id); отсутствует → все поля. */
  fields?: string;
  stage?: DealStage;
  /** Мультивыбор стадий (backend param `stages`, на проводе — CSV). */
  stages?: DealStage[];
  currency?: DealCurrency;
  ownerUserId?: string;
  departmentId?: string;
  /** Верхняя граница дат-диапазона (ISO Instant; смысл — по dateField). */
  dueBefore?: string;
  /** Нижняя граница дат-диапазона (ISO Instant; смысл — по dateField). */
  dueAfter?: string;
  /** Сортировка: "<field>,<asc|desc>", field ∈ createdAt|dueDate|amount|title|nextContactAt. */
  sort?: string;
  /** Колонка дат-диапазона dueBefore/dueAfter (дефолт backend: createdAt — U-27). */
  dateField?: DealDateField;
}

export interface DealCreateRequest {
  title: string;
  amount?: number | null;
  currency: DealCurrency;
  stage?: DealStage;
  ownerUserId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  departmentId: string;
  /** Плановая дата закрытия в формате "YYYY-MM-DD". */
  dueDate?: string | null;
  /** Описание сделки (optional, ≤4000 chars). */
  description?: string | null;
}

export interface DealUpdateRequest {
  title?: string;
  amount?: number | null;
  currency?: DealCurrency;
  stage?: DealStage;
  ownerUserId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  /** Плановая дата закрытия в формате "YYYY-MM-DD". */
  dueDate?: string | null;
  /** Описание сделки (optional, ≤4000 chars). */
  description?: string | null;
  expectedUpdatedAt: string;
}

export interface DealChangeStageRequest {
  /** Целевая стадия (backend-контракт DealChangeStageRequest.newStage, R24 / D23). */
  newStage: DealStage;
  expectedUpdatedAt: string;
  /** Обязателен при newStage=LOST (новый вход); иначе игнорируется. */
  rejectionReasonId?: string | null;
}

export interface DealAssignRequest {
  ownerUserId: string;
}

/** DTO комментария к сделке (wire-формат backend DealComment record, R18.4 — поле `body`). */
export interface DealComment {
  id: string;
  dealId: string;
  /** FK → users.id (NOT NULL на backend). */
  authorUserId: string;
  /** Текст комментария (1–5000 chars на backend; POST принимает поле `text`). */
  body: string;
  /** Момент создания (ISO Instant). */
  createdAt: string;
}

/** Deal comments API (GET list chronological ASC + POST add, FR-D9). */
export const dealCommentsApi = {
  /** GET /api/deals/{dealId}/comments?page&size → Page<DealComment> (ASC по createdAt). */
  async list(dealId: string, page = 0, size = 50): Promise<Page<DealComment>> {
    const { data } = await apiClient.get<Page<DealComment>>(`/api/deals/${dealId}/comments`, {
      params: { page, size },
    });
    return data;
  },

  /** POST /api/deals/{dealId}/comments, body {text} → 201 DealComment. */
  async add(dealId: string, text: string): Promise<DealComment> {
    const { data } = await apiClient.post<DealComment>(`/api/deals/${dealId}/comments`, { text });
    return data;
  },
};

/** Deals API. */
export const dealsApi = {
  /** GET /api/deals?filters → Page<Deal>. Массив stages сериализуется в CSV. */
  async list(filters: DealFilters = {}): Promise<Page<Deal>> {
    const { stages, ...rest } = filters;
    const { data } = await apiClient.get<Page<Deal>>('/api/deals', {
      params: { ...rest, ...(stages && stages.length > 0 ? { stages: stages.join(',') } : {}) },
    });
    return data;
  },

  /** GET /api/deals/{id} → Deal. */
  async get(id: string): Promise<Deal> {
    const { data } = await apiClient.get<Deal>(`/api/deals/${id}`);
    return data;
  },

  /** POST /api/deals → Deal. */
  async create(payload: DealCreateRequest): Promise<Deal> {
    const { data } = await apiClient.post<Deal>('/api/deals', payload);
    return data;
  },

  /** PUT /api/deals/{id} → Deal. */
  async update(id: string, payload: DealUpdateRequest): Promise<Deal> {
    const { data } = await apiClient.put<Deal>(`/api/deals/${id}`, payload);
    return data;
  },

  /**
   * DELETE /api/deals/{id}?expectedUpdatedAt=<Instant> — soft delete (FR-D6).
   * <p>
   * Backend {@code DealsController#delete} требует обязательный query-параметр
   * {@code expectedUpdatedAt} (без defaultValue → отсутствие = 400, B1).
   */
  async delete(id: string, expectedUpdatedAt: string): Promise<void> {
    await apiClient.delete(`/api/deals/${id}`, { params: { expectedUpdatedAt } });
  },

  /** POST /api/deals/{id}/stage → Deal. */
  async changeStage(id: string, payload: DealChangeStageRequest): Promise<Deal> {
    const { data } = await apiClient.post<Deal>(`/api/deals/${id}/stage`, payload);
    return data;
  },

  /** POST /api/deals/{id}/assign → Deal. */
  async assign(id: string, payload: DealAssignRequest): Promise<Deal> {
    const { data } = await apiClient.post<Deal>(`/api/deals/${id}/assign`, payload);
    return data;
  },

  /**
   * POST /api/deals/{id}/send-email → 202 SendEmailResponse{deliveryId, status, error?}.
   * Тело как у счёта: templateId обязателен, accountId опционален.
   */
  async sendEmail(dealId: string, payload: { templateId: string; accountId?: string }): Promise<SendEmailResponse> {
    const { data } = await apiClient.post<SendEmailResponse>(`/api/deals/${dealId}/send-email`, payload);
    return data;
  },
};
